import i18next, { type TOptions } from "i18next"
import BACKEND from "i18next-http-backend"
import ICU from "i18next-icu"
import { useCookies } from "@vueuse/integrations/useCookies"
import { i18nNamespaces } from "@/config/i18n"

import type { Translate } from "@/plugins/i18n/types"

// Setup translation module
const i18nextInstance = i18next.use(ICU).use(BACKEND)

const langInCookie = useCookies().get("locale")

export const initI18N = async (translationUrl: string) => {
  try {
    // Get path to load resources
    const loadPath = `${translationUrl}/{{lng}}/{{ns}}.json`

    // Init 18next
    await i18nextInstance.init({
      ns: i18nNamespaces,
      lng: langInCookie?.iso_code_639 ?? "en-US",
      fallbackLng: {
        default: ["en-US"],
      },
      load: "currentOnly",
      debug: false,
      cache: {
        enabled: true,
        prefix: "i18next_res_",
        expirationTime: 7 * 24 * 60 * 60 * 1000,
      },
      backend: {
        loadPath,
        parse(data: string) {
          return JSON.parse(data)
        },
      },
    })
  } catch (err) {
    console.error("erreur", err)
  }
}

const icuGender = (shortGender: string): string => {
  let gender = "female"
  if (shortGender === "m") {
    gender = "male"
  } else if (shortGender === "n") {
    gender = "other"
  }
  return gender
}

// this duplicated export is used in brain.js (jQuery) Don't know how to inject it in jQuery files
export const t: Translate = (
  key,
  options = undefined,
  checkShouldExist = false
) => {
  if (!key) return ""
  const newKey = key.replace(".", ":")
  // If needed, check if the key exists in the language files, if not return raw text
  if (checkShouldExist && !i18nextInstance.exists(newKey)) {
    return key
  }
  // If gender passed, format in icu format
  if (options && options.gender) {
    options.gender = icuGender(options.gender as string)
  }
  return i18nextInstance.t(newKey, options as Record<string, unknown>)
}

export const tAsync = async (
  key: string,
  forcedLng: string,
  options: TOptions = {}
): Promise<string> => {
  if (!key) return ""
  const newKey = key.replace(/\./g, ":")

  if (options.gender) {
    options.gender = icuGender(options.gender as string)
  }

  await i18nextInstance.loadLanguages(forcedLng)
  return i18nextInstance.t(newKey, { ...options, lng: forcedLng })
}

export const i18nPlugin = {
  install: (app) => {
    // function to format our translation keys into i18n format
    app.config.globalProperties.$t = t

    app.provide("t", t)
  },
}
